package com.sddamico.draftmachine.ds.navigation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmMotion
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.boxShadowRaw
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.displayInlineFlex
import com.sddamico.draftmachine.ds.dmStamp
import com.sddamico.draftmachine.ds.glowBrass
import com.sddamico.draftmachine.ds.spin
import com.sddamico.draftmachine.ds.transitionRaw
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** One station in a [Stepper]. */
data class StepItem(val label: String, val caption: String? = null)

/**
 * A geared wizard rail for the draft-setup flow. Completed cogs are brass, the current cog glows and
 * slowly spins, future stations are cold steel. Port of `components/navigation/Stepper.jsx`.
 */
@Composable
fun Stepper(
  steps: List<StepItem>,
  current: Int = 0,
  vertical: Boolean = false,
  modifier: Modifier = Modifier,
) {
  Div(
    attrs = modifier
      .displayFlex()
      .flexDirection(if (vertical) FlexDirection.Column else FlexDirection.Row)
      .alignItems(if (vertical) AlignItems.FlexStart else AlignItems.Center)
      .gap(if (vertical) DmSpacing.space5 else DmSpacing.space0)
      .toAttrs()
  ) {
    steps.forEachIndexed { i, s ->
      val done = i < current
      val active = i == current
      val cogColor = if (done || active) DmColor.brass500 else DmColor.steel600

      Div(
        attrs = Modifier
          .displayFlex()
          .flexDirection(if (vertical) FlexDirection.Row else FlexDirection.Column)
          .alignItems(AlignItems.Center)
          .gap(if (vertical) DmSpacing.space3 else DmSpacing.space2)
          .flex("0 0 auto")
          .toAttrs()
      ) {
        Span(
          attrs = Modifier
            .position(Position.Relative).displayInlineFlex()
            .alignItems(AlignItems.Center).justifyContent(JustifyContent.Center)
            .size(44.px).borderRadius(50.percent)
            .backgroundColor(DmColor.surfaceInset)
            .border(2.px, LineStyle.Solid, cogColor)
            .then(if (active) Modifier.glowBrass() else Modifier.boxShadowRaw("inset 0 1px 2px rgba(0,0,0,.5)"))
            .transitionRaw(
              "border-color ${DmMotion.durSlow} ${DmMotion.easeOut}, box-shadow ${DmMotion.durSlow} ${DmMotion.easeOut}"
            )
            .toAttrs()
        ) {
          val glyphMod = Modifier.fontSize(if (done) 20.px else 24.px).color(cogColor)
          // Keyed so a station that just finished swaps its cog for a freshly stamped check.
          key(done) {
            Icon(
              if (done) "check" else "gear-six",
              IconWeight.Fill,
              when {
                active -> glyphMod.spin()
                done -> glyphMod.styleModifier { property("animation", "dm-stamp-in 420ms ${DmMotion.easeSpring} both") }
                else -> glyphMod
              },
            )
          }
        }
        Div(attrs = Modifier.styleModifier { property("text-align", if (vertical) "left" else "center") }.toAttrs()) {
          Div(
            attrs = Modifier.dmStamp()
              .color(if (active) DmColor.brass300 else if (done) DmColor.textSecondary else DmColor.textMuted)
              .toAttrs()
          ) { Text(s.label) }
          if (s.caption != null) {
            Div(attrs = Modifier.fontSize(DmType.textXs).color(DmColor.textMuted).margin(top = 2.px).toAttrs()) {
              Text(s.caption)
            }
          }
        }
      }

      if (i < steps.size - 1) {
        val past = i < current
        if (vertical) {
          Span(
            attrs = Modifier.width(2.px).height(22.px).margin(left = 21.px)
              .connectorFill(past, vertical = true).toAttrs()
          ) {}
        } else {
          Span(
            attrs = Modifier.flex("1").height(2.px)
              .margin(leftRight = DmSpacing.space2).margin(top = 21.px)
              .styleModifier { property("align-self", "flex-start") }
              .connectorFill(past, vertical = false).toAttrs()
          ) {}
        }
      }
    }
  }
}

/** A rail segment that fills with brass in the direction of travel once its station is passed. */
private fun Modifier.connectorFill(past: Boolean, vertical: Boolean): Modifier = this
  .backgroundColor(DmColor.borderStrong)
  .styleModifier {
    property("background-image", "linear-gradient(${DmColor.brass600}, ${DmColor.brass600})")
    property("background-repeat", "no-repeat")
    val filled = if (past) "100%" else "0%"
    property("background-size", if (vertical) "100% $filled" else "$filled 100%")
    property("transition", "background-size 420ms ${DmMotion.easeSnap}")
  }
